<template>
  <div class="article-edit">
    <div class="edit-card">
      <h3 class="form-section-title">基本信息</h3>

      <div class="form-row">
        <div class="form-item full-width">
          <label>文章标题 <span class="required">*</span></label>
          <input v-model="form.title" type="text" placeholder="请输入文章标题" />
        </div>
      </div>

      <div class="form-row">
        <div class="form-item full-width">
          <label>文章主标题</label>
          <input v-model="form.main_title" type="text" placeholder="可选，独立展示于详情页顶部的醒目标题" />
        </div>
      </div>

      <div class="form-row">
        <div class="form-item">
          <label>价格</label>
          <input v-model="form.price" type="text" placeholder="如：免费、￥99、会员专享..." />
        </div>
        <div class="form-item">
          <label>日期</label>
          <input v-model="form.date" type="date" />
        </div>
      </div>

      <div class="form-row">
        <div class="form-item">
          <label>状态</label>
          <select v-model="form.status">
            <option :value="1">发布</option>
            <option :value="0">草稿</option>
          </select>
        </div>
        <div class="form-item">
          <label>类型</label>
          <input v-model="form.type" type="text" placeholder="如：文章、视频、图集..." />
        </div>
        <div class="form-item">
          <label>分类</label>
          <select v-model.number="form.category_id">
            <option :value="null">未分类</option>
            <option v-for="cat in categories" :key="cat.id" :value="cat.id">{{ cat.name }}</option>
          </select>
        </div>
        <div class="form-item">
          <label>排序（越小越靠前）</label>
          <input v-model.number="form.sort_order" type="number" placeholder="0" />
        </div>
      </div>

      <div class="form-item full-width">
        <label>摘要</label>
        <textarea v-model="form.summary" rows="2" placeholder="请输入文章摘要（将展示在列表页）"></textarea>
      </div>

      <h3 class="form-section-title">封面图片</h3>
      <div class="form-item">
        <label>列表页封面（首页卡片）<span class="size-hint">A4比例 160×226</span></label>
        <div class="upload-area">
          <div v-if="form.image" class="image-preview">
            <img :src="form.image" alt="封面预览" />
            <button class="btn-remove-img" @click="form.image = ''">移除</button>
          </div>
          <div v-else class="upload-trigger" @click="triggerUpload('fileInput')">
            <span class="upload-icon">+</span>
            <span>点击上传列表封面</span>
          </div>
          <input ref="fileInput" type="file" accept="image/*" style="display:none" @change="handleFileChange($event, 'image')" />
        </div>
        <input v-model="form.image" type="text" placeholder="或输入图片URL地址" />
      </div>

      <div class="form-item">
        <label>详情页封面（文章头部）<span class="size-hint">正方形 750×750</span></label>
        <div class="upload-area">
          <div v-if="form.detail_image" class="image-preview">
            <img :src="form.detail_image" alt="详情封面预览" />
            <button class="btn-remove-img" @click="form.detail_image = ''">移除</button>
          </div>
          <div v-else class="upload-trigger" @click="triggerUpload('detailFileInput')">
            <span class="upload-icon">+</span>
            <span>点击上传详情封面</span>
          </div>
          <input ref="detailFileInput" type="file" accept="image/*" style="display:none" @change="handleFileChange($event, 'detail_image')" />
        </div>
        <input v-model="form.detail_image" type="text" placeholder="或输入图片URL地址" />
      </div>

      <div class="form-item">
        <h3 class="form-section-title">分享设置</h3>
        <label>分享好友图片 <span class="size-hint">微信好友分享 5:4</span></label>
        <div class="upload-area">
          <div v-if="form.share_friend_image" class="image-preview share-preview">
            <img :src="form.share_friend_image" alt="好友分享图" />
            <button class="btn-remove-img" @click="form.share_friend_image = ''">移除</button>
          </div>
          <div v-else class="upload-trigger" @click="triggerUpload('shareFriendInput')">
            <span class="upload-icon">+</span>
            <span>点击上传好友分享图</span>
          </div>
          <input ref="shareFriendInput" type="file" accept="image/*" style="display:none" @change="handleFileChange($event, 'share_friend_image')" />
        </div>
        <input v-model="form.share_friend_image" type="text" placeholder="或输入图片URL地址，留空使用系统默认" />
      </div>

      <div class="form-item">
        <label>转发朋友圈图片 <span class="size-hint">微信朋友圈分享 1:1</span></label>
        <div class="upload-area">
          <div v-if="form.share_timeline_image" class="image-preview share-preview">
            <img :src="form.share_timeline_image" alt="朋友圈分享图" />
            <button class="btn-remove-img" @click="form.share_timeline_image = ''">移除</button>
          </div>
          <div v-else class="upload-trigger" @click="triggerUpload('shareTimelineInput')">
            <span class="upload-icon">+</span>
            <span>点击上传朋友圈分享图</span>
          </div>
          <input ref="shareTimelineInput" type="file" accept="image/*" style="display:none" @change="handleFileChange($event, 'share_timeline_image')" />
        </div>
        <input v-model="form.share_timeline_image" type="text" placeholder="或输入图片URL地址，留空使用系统默认" />
      </div>

      <h3 class="form-section-title">文章正文</h3>
      <div class="editor-wrapper">
        <Toolbar
          :editor="editor"
          :defaultConfig="toolbarConfig"
          mode="default"
        />
        <Editor
          v-model="editorHtml"
          :defaultConfig="editorConfig"
          mode="default"
          class="editor-body"
          @onCreated="onEditorCreated"
        />
      </div>

      <div class="form-actions">
        <button class="btn-cancel" @click="$router.back()">取消</button>
        <button v-if="isEdit" class="btn-default" @click="handleSave(0)">存为草稿</button>
        <button class="btn-primary" @click="handleSave(1)" :disabled="saving">
          {{ saving ? '保存中...' : (isEdit ? '更新发布' : '立即发布') }}
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import { request, BASE_URL } from '../api/index';
import '@wangeditor/editor/dist/css/style.css';
import { Editor, Toolbar } from '@wangeditor/editor-for-vue';

function plainToHtml(text) {
  if (!text) return '';
  if (/<[a-zA-Z][^>]*>/.test(text)) return text;
  return '<p>' + text.split('\n').map(function (p) {
    return p.trim() ? p : '<br>';
  }).join('</p><p>') + '</p>';
}

export default {
  name: 'ArticleEdit',
  components: { Editor, Toolbar },
  data() {
    return {
      form: {
        id: null,
        title: '',
        main_title: '',
        price: '',
        date: new Date().toISOString().slice(0, 10),
        status: 1,
        sort_order: 0,
        category_id: null,
        type: '',
        summary: '',
        image: '',
        detail_image: '',
        share_friend_image: '',
        share_timeline_image: '',
        content: '',
      },
      saving: false,
      isEdit: false,
      editor: null,
      editorHtml: '',
      categories: [],
      toolbarConfig: {
        excludeKeys: [
          'group-video',
        ],
      },
      editorConfig: {
        placeholder: '请输入文章正文内容...',
        autoFocus: false,
        MENU_CONF: {
          uploadImage: {
            server: BASE_URL + '/upload.php',
            fieldName: 'file',
            maxFileSize: 5 * 1024 * 1024,
            allowedFileTypes: ['image/*'],
            customInsert: function (res, insertFn) {
              if (res.code === 0) {
                insertFn(res.data.url, res.data.filename, res.data.url);
              }
            },
            customUpload: function (file, insertFn) {
              var formData = new FormData();
              formData.append('file', file);
              var token = localStorage.getItem('admin_token');
              var xhr = new XMLHttpRequest();
              xhr.open('POST', BASE_URL + '/upload.php');
              xhr.setRequestHeader('Authorization', 'Bearer ' + token);
              xhr.onload = function () {
                try {
                  var data = JSON.parse(xhr.responseText);
                  if (data.code === 0) {
                    insertFn(data.data.url, data.data.filename, data.data.url);
                  }
                } catch (e) {
                  // ignore
                }
              };
              xhr.send(formData);
            },
          },
        },
      },
    };
  },
  async created() {
    var id = this.$route.params.id;
    await this.fetchCategories();
    if (id) {
      this.isEdit = true;
      await this.loadArticle(id);
    } else {
      this.editorHtml = '';
    }
  },
  beforeDestroy() {
    var editor = this.editor;
    if (editor == null) return;
    editor.destroy();
    this.editor = null;
  },
  methods: {
    async fetchCategories() {
      try {
        var res = await request('/categories.php');
        this.categories = res.data || [];
      } catch (e) {
        // ignore
      }
    },
    onEditorCreated(editor) {
      this.editor = Object.seal(editor);
    },
    async loadArticle(id) {
      try {
        var res = await request('/articles.php?id=' + id);
        var article = res.data;
        this.form = {
          id: article.id,
          title: article.title,
          main_title: article.main_title || '',
          price: article.price || '',
          author: article.author,
          date: article.date,
          status: parseInt(article.status),
          sort_order: parseInt(article.sort_order),
          category_id: article.category_id,
          type: article.type || '',
          summary: article.summary,
          image: article.image,
          detail_image: article.detail_image || '',
          share_friend_image: article.share_friend_image || '',
          share_timeline_image: article.share_timeline_image || '',
          content: article.content,
        };
        this.editorHtml = plainToHtml(article.content);
      } catch (e) {
        alert('加载文章失败: ' + e.message);
        this.$router.back();
      }
    },
    triggerUpload(refName) {
      this.$refs[refName].click();
    },
    async handleFileChange(e, field) {
      var file = e.target.files[0];
      if (!file) return;
      var formData = new FormData();
      formData.append('file', file);
      try {
        var token = localStorage.getItem('admin_token');
        var res = await fetch(BASE_URL + '/upload.php', {
          method: 'POST',
          headers: { Authorization: 'Bearer ' + token },
          body: formData,
        });
        var data = await res.json();
        if (data.code === 0) {
          this.form[field] = data.data.url;
        } else {
          alert('上传失败: ' + data.msg);
        }
      } catch (err) {
        alert('上传失败: ' + err.message);
      }
    },
    async handleSave(status) {
      if (!this.form.title.trim()) {
        alert('请输入文章标题');
        return;
      }

      this.form.content = this.editorHtml;
      this.form.status = status;
      this.saving = true;

      try {
        var method = this.isEdit ? 'PUT' : 'POST';
        await request('/articles.php', {
          method: method,
          body: this.form,
        });
        alert('保存成功');
        this.$router.push('/articles');
      } catch (e) {
        alert('保存失败: ' + e.message);
      } finally {
        this.saving = false;
      }
    },
  },
};
</script>

<style scoped>
.article-edit {
  max-width: 1000px;
}

.edit-card {
  background: #fff;
  border-radius: 8px;
  padding: 32px;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.05);
}

.form-section-title {
  font-size: 16px;
  font-weight: 500;
  color: #333;
  margin: 28px 0 16px;
  padding-bottom: 8px;
  border-bottom: 1px solid #f0f0f0;
}

.form-section-title:first-child {
  margin-top: 0;
}

.form-row {
  display: flex;
  gap: 20px;
  margin-bottom: 16px;
}

.form-item {
  flex: 1;
  margin-bottom: 16px;
}

.form-item.full-width {
  flex: none;
  width: 100%;
}

.form-item label {
  display: block;
  margin-bottom: 6px;
  font-size: 14px;
  color: #555;
}

.required {
  color: #ff4d4f;
}

.form-item input[type="text"],
.form-item input[type="number"],
.form-item input[type="date"],
.form-item input[type="url"],
.form-item select,
.form-item textarea {
  width: 100%;
  padding: 8px 12px;
  border: 1px solid #d9d9d9;
  border-radius: 4px;
  font-size: 14px;
  outline: none;
  font-family: inherit;
  transition: border-color 0.3s;
}

.form-item input:focus,
.form-item select:focus,
.form-item textarea:focus {
  border-color: #1677FF;
  box-shadow: 0 0 0 2px rgba(22, 119, 255, 0.1);
}

.form-item select {
  height: 38px;
  background: #fff;
}

.form-item textarea {
  resize: vertical;
  line-height: 1.8;
  min-height: 100px;
}

.size-hint {
  font-size: 11px;
  color: #bbb;
  font-weight: 400;
  margin-left: 8px;
}

.upload-area {
  margin-bottom: 8px;
}

.upload-trigger {
  width: 160px;
  height: 100px;
  border: 2px dashed #d9d9d9;
  border-radius: 6px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: #999;
  transition: border-color 0.3s;
}

.upload-trigger:hover {
  border-color: #1677FF;
  color: #1677FF;
}

.upload-icon {
  font-size: 28px;
  line-height: 1;
  margin-bottom: 4px;
}

.image-preview {
  position: relative;
  display: inline-block;
  border-radius: 6px;
  overflow: hidden;
  border: 1px solid #f0f0f0;
}

.image-preview img {
  width: 160px;
  height: 226px;
  object-fit: cover;
  display: block;
}

.share-preview img {
  width: 200px;
  height: 160px;
}

.btn-remove-img {
  position: absolute;
  top: 4px;
  right: 4px;
  padding: 2px 8px;
  background: rgba(0, 0, 0, 0.5);
  color: #fff;
  border: none;
  border-radius: 3px;
  cursor: pointer;
  font-size: 12px;
}

.editor-wrapper {
  border: 1px solid #d9d9d9;
  border-radius: 4px;
  overflow: hidden;
}

.editor-wrapper:focus-within {
  border-color: #1677FF;
  box-shadow: 0 0 0 2px rgba(22, 119, 255, 0.1);
}

.editor-body {
  height: 500px;
  overflow-y: hidden;
}

.form-actions {
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  margin-top: 32px;
  padding-top: 20px;
  border-top: 1px solid #f0f0f0;
}

.btn-primary {
  height: 38px;
  padding: 0 24px;
  background: #1677FF;
  color: #fff;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 14px;
}

.btn-primary:hover {
  background: #4096ff;
}

.btn-primary:disabled {
  background: #a0c4ff;
  cursor: not-allowed;
}

.btn-default {
  height: 38px;
  padding: 0 24px;
  background: #fff;
  color: #333;
  border: 1px solid #d9d9d9;
  border-radius: 4px;
  cursor: pointer;
  font-size: 14px;
}

.btn-default:hover {
  border-color: #1677FF;
  color: #1677FF;
}

.btn-cancel {
  height: 38px;
  padding: 0 24px;
  background: #f0f0f0;
  color: #666;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 14px;
}

.btn-cancel:hover {
  background: #e0e0e0;
}
</style>
